import _ from "lodash";
import * as React from "react";
import {
  RoleName,
  Subscription,
  UserRoleName,
  UserRoles,
} from "../../interfaces/common";

export interface Session {
  user: {
    id: string;
    email: string;
    firstname?: string;
    lastname?: string;
  };
  org: {
    id: string;
    name: string;
  };
  roles?: UserRoles;
  accessToken: string;
  OrionUserGroups: string[];
  OrionContributorGroups: string[];
  OrionManagerGroups: string[];
  OrionChatGroups: string[];
  SiriusUserGroups: string[];
  SiriusContributorGroups: string[];
  SiriusManagerGroups: string[];
  ManagerGroups: string[];
  isAdmin: boolean;
}

interface SessionContextType {
  session: Session | undefined;
  setSession: React.Dispatch<React.SetStateAction<Session | undefined>>;
  removeSession: () => void;
}

export const SessionContext = React.createContext<SessionContextType>({
  session: undefined,
  setSession: () => {},
  removeSession: () => {},
});

/**
 * Hook to access the current Toolpad Core session.
 * @returns The current session object or null if no session is available.
 */
export const useSession = () => React.useContext(SessionContext);

export function OrionUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [Subscription.ORION, Subscription.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [Subscription.ORION, Subscription.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [Subscription.ORION, Subscription.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function OrionChatGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => Subscription.PLATFORM === role.subscription)
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusUserGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) =>
          [Subscription.SIRIUS, Subscription.PLATFORM].includes(
            role.subscription
          )
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusContributorGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [Subscription.SIRIUS, Subscription.PLATFORM].includes(
              role.subscription
            ) && [RoleName.CONTRIBUTOR, RoleName.MANAGER].includes(role.role)
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function SiriusManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(
          ([_, role]) =>
            [Subscription.SIRIUS, Subscription.PLATFORM].includes(
              role.subscription
            ) && RoleName.MANAGER === role.role
        )
        .map(([workspace, _]) => workspace)
    : [];
}

export function ManagerGroups(roles: UserRoles | undefined) {
  return !!roles
    ? _.entries(roles.workspaces)
        .filter(([_, role]) => role.role === RoleName.MANAGER)
        .map(([workspace, _]) => workspace)
    : [];
}

export function isAdmin(roles: UserRoles | undefined) {
  return !!roles && roles.role === UserRoleName.ADMIN;
}
